import { useEffect } from 'react';
import { useFetcher } from '@remix-run/react';

interface TrendingSearchResult {
    id: string;
    title: string;
    link: string;
    type: 'Blog' | 'Product' | 'Professional' | 'Project';
}

interface FeaturedResultsResponse {
    results: Array<{
        title: string;
        link: string;
        type: 'Blog' | 'Product' | 'Professional' | 'Project';
        [key: string]: unknown;
    }>;
    totalCount: number;
}

interface CachedTrendingResults {
    results: TrendingSearchResult[];
    timestamp: number;
}

const CACHE_KEY = 'trending_search_results';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Hook to prefetch trending search results on mobile for non-bot users.
 * This runs automatically when the app loads to cache results in advance,
 * improving performance when users open the mobile search modal.
 */
export const usePrefetchTrendingResults = (
    isMobile: boolean,
    isBot: boolean
): void => {
    const fetcher = useFetcher<FeaturedResultsResponse>();

    // Function to check if cached results are still valid
    const getCachedResults = (): TrendingSearchResult[] | null => {
        if (typeof window === 'undefined') return null;

        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (!cached) return null;

            const cachedData: CachedTrendingResults = JSON.parse(cached);
            const now = Date.now();

            // Check if cache is still valid (within 30 minutes)
            if (now - cachedData.timestamp < CACHE_DURATION) {
                return cachedData.results;
            }

            // Cache expired, remove it
            sessionStorage.removeItem(CACHE_KEY);
            return null;
        } catch (error) {
            // If there's any error parsing cached data, remove it
            if (typeof window !== 'undefined') {
                sessionStorage.removeItem(CACHE_KEY);
            }
            return null;
        }
    };

    // Function to cache results in session storage
    const cacheResults = (results: TrendingSearchResult[]) => {
        if (typeof window === 'undefined') return;

        try {
            const cacheData: CachedTrendingResults = {
                results,
                timestamp: Date.now(),
            };
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            // Silently fail if session storage is not available or full
            console.warn('Failed to cache trending search results:', error);
        }
    };

    useEffect(() => {
        // Only run on client side
        if (typeof window === 'undefined') return;

        // Only prefetch on mobile
        if (!isMobile) return;

        // Don't prefetch for bots
        if (isBot) return;

        // Check if we already have valid cached results
        const cachedResults = getCachedResults();
        if (cachedResults && cachedResults.length > 0) {
            // Already have valid cached results, no need to prefetch
            return;
        }

        // Prefetch trending results with a small delay to not interfere with critical loading
        const timeoutId = setTimeout(() => {
            const params = new URLSearchParams({
                limit: '5',
            });
            fetcher.load(
                `/actions/search/featured-results?${params.toString()}`
            );
        }, 200); // 100ms delay

        return () => clearTimeout(timeoutId);
    }, [isMobile, fetcher, isBot]);

    // Handle the fetcher response
    useEffect(() => {
        if (fetcher.data?.results && fetcher.state === 'idle') {
            // Convert featured results to trending search results
            const results: TrendingSearchResult[] = fetcher.data.results
                .slice(0, 5)
                .map((result, index: number) => ({
                    id: `trending-${index}`,
                    title: result.title,
                    link: result.link,
                    type: result.type,
                }));

            // Cache the prefetched results
            cacheResults(results);
        }
    }, [fetcher.data, fetcher.state]);
};
